import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { User, Mail, Key, Save } from 'lucide-react';
import './ProfilePage.css';

function ProfilePage() {
  const { user, updateProfile, changePassword, isLoading, error, clearError } = useAuth();
  const { t } = useLanguage();
  const [activeTab, setActiveTab] = useState('profile');
  const [success, setSuccess] = useState('');
  const [passwordValidationError, setPasswordValidationError] = useState('');

  // Profile form state
  const [profileData, setProfileData] = useState({
    firstName: '',
    lastName: '',
    email: ''
  });

  // Password form state
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  // Initialize form data when user data is available
  useEffect(() => {
    if (user) {
      setProfileData({
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        email: user.email || ''
      });
    }
  }, [user]);

  // Clear messages when switching tabs
  useEffect(() => {
    clearError();
    setSuccess('');
  }, [activeTab, clearError]); // Now safe to include clearError since it's memoized

  // Password validation function
  const validatePassword = (password) => {
    const minLength = 8;
    const hasUpperCase = /[A-Z]/.test(password);
    const hasLowerCase = /[a-z]/.test(password);
    const hasNumbers = /\d/.test(password);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);

    if (password.length < minLength) {
      return 'Password must be at least 8 characters long';
    }
    if (!hasUpperCase) {
      return 'Password must contain at least one uppercase letter';
    }
    if (!hasLowerCase) {
      return 'Password must contain at least one lowercase letter';
    }
    if (!hasNumbers) {
      return 'Password must contain at least one number';
    }
    if (!hasSpecialChar) {
      return 'Password must contain at least one special character';
    }
    return null; // Valid password
  };

  const handleProfileChange = (e) => {
    const { name, value } = e.target;
    setProfileData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData(prev => ({
      ...prev,
      [name]: value
    }));

    // Validate password in real-time
    if (name === 'newPassword') {
      const validationError = validatePassword(value);
      setPasswordValidationError(validationError || '');
    }
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    
    try {
      await updateProfile(profileData);
      setSuccess(t('profile.profileUpdated'));
    } catch (error) {
      // Error handled by context
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    
    // Clear previous messages
    clearError();
    setSuccess('');
    
    // Validate password match
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      return;
    }

    // Validate new password requirements
    const validationError = validatePassword(passwordData.newPassword);
    if (validationError) {
      setPasswordValidationError(validationError);
      return;
    }

    try {
      await changePassword(passwordData.currentPassword, passwordData.newPassword);
      setSuccess(t('profile.passwordChanged'));
      setPasswordValidationError('');
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
    } catch (error) {
      // Error handled by context
    }
  };

  if (!user) {
    return (
      <div className="profile-page">
        <div className="profile-container">
          <div className="loading">{t('profile.loading')}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="profile-page">
      {/* Updated header without back button */}
      <div className="page-header">
        <div className="header-content">
          <h1>{t('profile.title')}</h1>
          <p>{t('profile.subtitle')}</p>
        </div>
      </div>

      <div className="profile-container">
        <div className="profile-content">
          <div className="profile-tabs">
            <button
              className={`tab ${activeTab === 'profile' ? 'active' : ''}`}
              onClick={() => setActiveTab('profile')}
            >
              <User size={18} />
              {t('profile.tabProfile')}
            </button>
            <button
              className={`tab ${activeTab === 'password' ? 'active' : ''}`}
              onClick={() => setActiveTab('password')}
            >
              <Key size={18} />
              {t('profile.tabPassword')}
            </button>
          </div>

          <div className="tab-content">
            {/* Success Message */}
            {success && (
              <div className="alert alert-success">
                {success}
              </div>
            )}

            {/* Error Message */}
            {error && (
              <div className="alert alert-error">{error}</div>
            )}

            {/* Profile Tab */}
            {activeTab === 'profile' && (
              <div className="profile-form-section">
                <h2>{t('profile.personalInfo')}</h2>
                <form onSubmit={handleProfileSubmit} className="profile-form">
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="firstName">{t('profile.firstName')}</label>
                      <input
                        type="text"
                        id="firstName"
                        name="firstName"
                        value={profileData.firstName}
                        onChange={handleProfileChange}
                        placeholder={t('profile.firstNamePlaceholder')}
                      />
                    </div>
                    <div className="form-group">
                      <label htmlFor="lastName">{t('profile.lastName')}</label>
                      <input
                        type="text"
                        id="lastName"
                        name="lastName"
                        value={profileData.lastName}
                        onChange={handleProfileChange}
                        placeholder={t('profile.lastNamePlaceholder')}
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor="email">
                      <Mail size={16} />
                      {t('profile.emailAddress')}
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={profileData.email}
                      disabled
                      className="disabled"
                    />
                    <p className="form-help">{t('profile.emailCannotChange')}</p>
                  </div>

                  <button 
                    type="submit" 
                    className="save-button"
                    disabled={isLoading}
                  >
                    <Save size={18} />
                    {isLoading ? t('profile.saving') : t('profile.saveChanges')}
                  </button>
                </form>
              </div>
            )}

            {/* Password Tab */}
            {activeTab === 'password' && (
              <div className="password-form-section">
                <h2>{t('profile.changePasswordTitle')}</h2>
                <form onSubmit={handlePasswordSubmit} className="password-form">
                  <div className="form-group">
                    <label htmlFor="currentPassword">{t('profile.currentPassword')}</label>
                    <input
                      type="password"
                      id="currentPassword"
                      name="currentPassword"
                      value={passwordData.currentPassword}
                      onChange={handlePasswordChange}
                      placeholder={t('profile.currentPasswordPlaceholder')}
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="newPassword">{t('profile.newPassword')}</label>
                    <input
                      type="password"
                      id="newPassword"
                      name="newPassword"
                      value={passwordData.newPassword}
                      onChange={handlePasswordChange}
                      placeholder={t('profile.newPasswordPlaceholder')}
                      required
                    />
                    <p className="form-help">
                      {t('profile.passwordRequirements')}
                    </p>
                    {passwordValidationError && (
                      <p className="form-error">{passwordValidationError}</p>
                    )}
                  </div>

                  <div className="form-group">
                    <label htmlFor="confirmPassword">{t('profile.confirmPassword')}</label>
                    <input
                      type="password"
                      id="confirmPassword"
                      name="confirmPassword"
                      value={passwordData.confirmPassword}
                      onChange={handlePasswordChange}
                      placeholder={t('profile.confirmPasswordPlaceholder')}
                      required
                    />
                    {passwordData.newPassword && passwordData.confirmPassword && 
                     passwordData.newPassword !== passwordData.confirmPassword && (
                      <p className="form-error">{t('profile.passwordsMismatch')}</p>
                    )}
                  </div>

                  <button 
                    type="submit" 
                    className="save-button"
                    disabled={
                      isLoading || 
                      passwordData.newPassword !== passwordData.confirmPassword ||
                      passwordValidationError ||
                      !passwordData.currentPassword ||
                      !passwordData.newPassword ||
                      !passwordData.confirmPassword
                    }
                  >
                    <Key size={18} />
                    {isLoading ? t('profile.changing') : t('profile.changePasswordButton')}
                  </button>
                </form>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default ProfilePage;
